// src/Components/Chat/ChatMessage.jsx
import React from "react";

// Regular player chat message component
const ChatMessage = ({ message, isOwnMessage, currentUserId }) => {
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return "just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    // For older messages, show time
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwn = message.userId === currentUserId;

  return (
    <div className={`mb-3 ${isOwn ? 'flex justify-end' : 'flex justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
        isOwn 
          ? 'bg-red-600 text-white' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {/* Player name (only show for others' messages) */}
        {!isOwn && (
          <div className="text-xs font-semibold text-gray-600 mb-1">
            {message.playerName}
          </div>
        )}
        
        {/* Message content */}
        <div className="text-sm break-words">
          {message.content}
        </div>
        
        {/* Timestamp */}
        <div className={`text-xs mt-1 ${
          isOwn ? 'text-red-100' : 'text-gray-500'
        }`}>
          {formatTimestamp(message.timestamp)}
        </div>
      </div>
    </div>
  );
};

// System message component (game events, notifications)
// A quiet, centred pill like WhatsApp's "X left the group" line: no box, no
// timestamp on the row (the time is in the hover title), so a run of system
// updates takes a line each instead of a card each.
const SystemMessage = ({ message }) => {
  const time = (() => {
    const { timestamp } = message;
    if (!timestamp) return undefined;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  })();

  return (
    <div className="my-1.5 flex justify-center px-3">
      <span
        title={time}
        className="max-w-[85%] rounded-xl bg-gray-100 px-3 py-1 text-center text-xs leading-snug text-gray-500"
      >
        {message.content}
      </span>
    </div>
  );
};

// Combined message component that chooses the right display
const Message = ({ message, currentUserId }) => {
  // Validate message object
  if (!message || !message.type) {
    console.warn("Invalid message object:", message);
    return null;
  }

  // Render based on message type
  if (message.type === "system") {
    return <SystemMessage message={message} />;
  }
  
  if (message.type === "chat") {
    return (
      <ChatMessage 
        message={message} 
        currentUserId={currentUserId}
        isOwnMessage={message.userId === currentUserId}
      />
    );
  }

  // Fallback for unknown message types
  console.warn("Unknown message type:", message.type);
  return null;
};

// Export both individual components and the combined one
export { ChatMessage, SystemMessage };
export default Message;